import {useContext, useLayoutEffect, useMemo, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import PropTypes from "prop-types";
import styled from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faStar as faStarFilled} from "@fortawesome/free-solid-svg-icons";
import {faStar} from "@fortawesome/free-regular-svg-icons";

// Contexts
import {NotificationContext} from "../../contexts/notify";
import {AuthContext} from "../../contexts/auth";
import {SettingsContext} from "../../contexts/settings";

// Utils
import {prettyDateInUserTimezone, slugify} from "../../utils/helpers";

// Components
import Badge from "../../components/Badge";
import Select from "../../components/Select";
import ModalFacilityFavorite from "../facilities/ModalFacilityFavorite";

// Style
import {slideLeft} from "../../style/components/animations";
import {z} from "../../style/components/mixins";
import {voice} from "../../style/components/typography";
import {heroTheme, pad} from "../../style/components/variables";
import {Abbr, Pill, Title} from "../../style/components/general";

const FacilityPageHeader = ({facility, path = "", reloadData, disabled = false}) => {
  const {name, type, types, isDeleted, updatedAt, updatedBy} = facility;

  const {currentUser} = useContext(AuthContext);
  const {notifications} = useContext(NotificationContext);
  const {settings} = useContext(SettingsContext);

  const [showModalFavorite, setShowModalFavorite] = useState(false);
  const [showMarquee, setShowMarquee] = useState(false);

  const nameRef = useRef(null);

  const navigate = useNavigate();

  const getCount = id => {
    if (!path?.includes("tasks") || !notifications?.otherTypes) return "";

    const count = notifications.otherTypes[`${id}`]?.totalDueCount;

    if (!count) return "";

    return ` (${count})`;
  };

  const totalCount = useMemo(() => {
    if (!path?.includes("tasks") || !notifications?.otherTypes) return null;

    let count = 0;

    Object.keys(notifications.otherTypes).map(id => {
      count += notifications.otherTypes[`${id}`]?.totalDueCount || 0;
    });

    return count;
  }, [notifications, path]);

  const favorites = useMemo(() => currentUser?.favorites || [], [currentUser]);

  useLayoutEffect(() => {
    const el = nameRef.current;
    if (!el) return undefined;

    const measure = () => setShowMarquee(el.offsetWidth < el.scrollWidth);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [name]);

  const archivedLabel = useMemo(() => {
    if (!isDeleted) return null;

    const by =
      updatedBy?.firstName || updatedBy?.lastName
        ? ` by ${[updatedBy.firstName, updatedBy.lastName].filter(Boolean).join(" ")}`
        : "";
    const on =
      updatedAt && updatedAt !== "None"
        ? ` on ${prettyDateInUserTimezone(updatedAt, settings?.timezone, "MMM D YYYY, h:mm A")}`
        : "";

    if (!by && !on) return null;
    return `Archived${by}${on}`;
  }, [isDeleted, updatedAt, updatedBy, settings?.timezone]);

  return (
    <>
      <PageTitle>
        <NameSlot $showMarquee={showMarquee}>
          {showMarquee && (
            <Marquee>
              <span>{name.toUpperCase()}</span>
              &nbsp;
              <span>{name.toUpperCase()}</span>
            </Marquee>
          )}
          <Name ref={nameRef} title={name}>
            {name.toUpperCase()}
          </Name>
        </NameSlot>
        {!facility?.isDeleted && facility.type && (
          <Wrapper>
            <Favorite type="button" onClick={() => setShowModalFavorite(true)}>
              <FontAwesomeIcon
                icon={favorites?.includes(facility.id) ? faStarFilled : faStar}
                className="primaryIcon">
                Favorite Facility
              </FontAwesomeIcon>
            </Favorite>
          </Wrapper>
        )}
        {facility?.type && (
          <Wrapper>
            <BadgeWrapper>
              <Select
                large
                options={types.map(({name: typeName, id}) => ({
                  label: `${typeName}${getCount(id)}`,
                  name: typeName
                }))}
                selection={type}
                setSelection={target => {
                  const targetPath = `/facilities/${slugify(`${name} ${target.name}`)}${
                    path || ""
                  }`;
                  if (window.location.pathname !== targetPath) {
                    navigate(targetPath);
                    if (reloadData) reloadData();
                  }
                }}
                disabled={disabled || types.length === 1}
              />
              {!!totalCount && (
                <Badge
                  title="Total number of checksheets due on the current type"
                  offsetX="-10px"
                  offsetY="5px"
                  color={heroTheme.secondary}>
                  {totalCount}
                </Badge>
              )}
            </BadgeWrapper>
          </Wrapper>
        )}
        <Wrapper>
          {isDeleted && (
            <Pill color={heroTheme.warning} data-testid="facility.archived">
              <Abbr title={archivedLabel || "Archived"}>ARCHIVED</Abbr>
            </Pill>
          )}
        </Wrapper>
      </PageTitle>

      {showModalFavorite && (
        <ModalFacilityFavorite
          visible={showModalFavorite}
          setVisible={setShowModalFavorite}
          facility={facility}
          reloadFacilities={() => setShowModalFavorite(false)}
        />
      )}
    </>
  );
};

FacilityPageHeader.propTypes = {
  facility: PropTypes.objectOf(PropTypes.any).isRequired,
  path: PropTypes.string,
  reloadData: PropTypes.func,
  disabled: PropTypes.bool
};

// Style Overrides
const PageTitle = styled(Title)`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
`;

const Name = styled.span`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Marquee = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  display: none;
  white-space: nowrap;
  animation: ${slideLeft} linear 10s infinite;
`;

const NameSlot = styled.span`
  position: relative;
  min-width: 0;
  overflow: hidden;

  &:hover {
    ${Name} {
      opacity: ${({$showMarquee}) => ($showMarquee ? 0 : 1)};
    }

    ${Marquee} {
      display: inline-flex;
    }
  }
`;

const Favorite = styled.button`
  width: inherit;
  display: inherit;
  padding: 0 ${pad}px;
  background: transparent;
  z-index: ${z("top")};

  .primaryIcon {
    ${voice.quiet};
    /* allow a larger click area */
    position: absolute;
    top: 0;
    right: 0;
    padding: ${pad / 2}px;
    fill: ${({theme}) => theme.secondary};
  }
`;

const Wrapper = styled.span`
  position: relative;
  display: inline-block;
  flex-shrink: 0;
  vertical-align: middle;
  margin-left: ${pad}px;
`;

const BadgeWrapper = styled.div`
  display: flex;
`;

export default FacilityPageHeader;
